/**
 * Adaptive music. Punk/ska-adjacent, generated, 168 BPM.
 *
 * Crazy Taxi's musical identity is Bad Religion and The Offspring, so the brief is: driving
 * eighth notes, a snare on 2 and 4 that cracks, power chords, and enough harmonic movement
 * that it does not become wallpaper.
 *
 * [MEASURED] against the reference longplay: tempo estimates came back 152–172 BPM across
 * different tracks; 168 sits in the middle. Every chord root in `form` was found in the
 * reference bass spectrum (C2 64.6, D2 72.7, E2 83.4, F2 87.3, G2 94.2, A2 110.4 Hz).
 *
 * HOW IT AVOIDS BEING ONE BAR ON REPEAT:
 *   · The form is SIXTEEN bars long with four distinct four-bar phrases: i-VI-III-VII, then
 *     the same but landing on a major V (E) for the punk lift, then a borrowed D major
 *     (the Dorian brightener), then a turnaround.
 *   · FOUR macro sections — punk / ska / drive / breakdown — rotate on every pass, so the
 *     same bar of the form never sounds the same two passes running. Full cycle before
 *     literal repetition: 16 bars × 4 sections ≈ 1 min 32 s.
 *   · Intensity adds and removes layers and pushes the tempo, so the arrangement also tracks
 *     what the player is doing.
 *
 * SCHEDULING: a standard lookahead scheduler. `update()` (presentation) pushes note events
 * into the AudioContext timeline up to `lookahead` seconds ahead. Nothing depends on frame
 * rate; a 20 fps stutter does not drop a beat.
 */

import { AUDIO_TUNING as T, mtof } from './tuning.js';
import { makeDriveCurve } from './dsp.js';

const M = T.music;
const STEPS_PER_BAR = 16;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Layer audibility from intensity. */
function layerGain(spec, intensity) {
  if (intensity <= spec.from) return 0;
  const t = clamp01((intensity - spec.from) / Math.max(0.001, spec.fade));
  return spec.gain * t;
}

/** A monophonic subtractive voice with persistent nodes. Notes are param automation only. */
class SynthVoice {
  constructor(a, dest, spec, oscCount, detunes) {
    this.spec = spec;
    this.oscs = [];
    this.amp = a.createGain();
    this.amp.gain.value = 0.0001;

    this.filter = a.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = spec.lpBase || spec.lp || 1200;
    this.filter.Q.value = spec.q || 1;
    this.amp.connect(this.filter);

    let tail = this.filter;
    if (spec.drive) {
      this.shaper = a.createWaveShaper();
      this.shaper.curve = makeDriveCurve(spec.drive);
      this.shaper.oversample = '2x';
      this.filter.connect(this.shaper);
      tail = this.shaper;
    }
    if (spec.hp) {
      this.hp = a.createBiquadFilter();
      this.hp.type = 'highpass';
      this.hp.frequency.value = spec.hp;
      tail.connect(this.hp);
      tail = this.hp;
    }
    // Post-drive trim: a waveshaper raises apparent level a lot.
    this.trim = a.createGain();
    this.trim.gain.value = spec.drive ? 0.45 : 1;
    tail.connect(this.trim);
    this.trim.connect(dest);

    const n = oscCount || 1;
    for (let i = 0; i < n; i++) {
      const o = a.createOscillator();
      o.type = spec.type;
      o.frequency.value = 110;
      o.detune.value = detunes ? detunes[i % detunes.length] : 0;
      o.connect(this.amp);
      o.start(a.currentTime);
      this.oscs.push(o);
    }
    if (spec.sub) {
      this.subOsc = a.createOscillator();
      this.subOsc.type = spec.sub;
      this.subOsc.frequency.value = 55;
      this.subGain = a.createGain();
      this.subGain.gain.value = spec.subGain || 0.4;
      this.subOsc.connect(this.subGain);
      this.subGain.connect(this.amp);
      this.subOsc.start(a.currentTime);
    }
    this.freeAt = 0;
  }

  /**
   * @param {number} when   audio-clock start time
   * @param {number[]} freqs one per oscillator (voicing)
   * @param {number} dur    seconds until release starts
   * @param {number} vel    0..1
   */
  note(when, freqs, dur, vel) {
    const s = this.spec;
    for (let i = 0; i < this.oscs.length; i++) {
      this.oscs[i].frequency.setValueAtTime(freqs[Math.min(i, freqs.length - 1)], when);
    }
    if (this.subOsc) this.subOsc.frequency.setValueAtTime(freqs[0] * 0.5, when);

    const cutoff = (s.lpBase || s.lp || 1200) + (s.lpPerVel || 0) * vel;
    this.filter.frequency.setValueAtTime(Math.min(16000, cutoff), when);

    const g = this.amp.gain;
    const peak = Math.max(0.0002, vel);
    const atk = s.attack || 0.004;
    const rel = s.release || 0.05;
    const dec = s.decay || 0;
    const sus = s.sustain == null ? 1 : s.sustain;
    g.cancelScheduledValues(when);
    g.setValueAtTime(0.0001, when);
    g.linearRampToValueAtTime(peak, when + atk);
    if (dec > 0) g.exponentialRampToValueAtTime(Math.max(0.0002, peak * sus), when + atk + dec);
    const off = when + Math.max(atk + 0.005, dur);
    g.setValueAtTime(Math.max(0.0002, peak * (dec > 0 ? sus : 1)), off);
    g.linearRampToValueAtTime(0.0001, off + rel);
    this.freeAt = off + rel;
  }

  silence(when) {
    const g = this.amp.gain;
    g.cancelScheduledValues(when);
    g.setValueAtTime(Math.max(0.0001, g.value), when);
    g.linearRampToValueAtTime(0.0001, when + 0.06);
    this.freeAt = when;
  }
}

export class MusicEngine {
  constructor(graph, randTable) {
    this.graph = graph;
    this.rand = randTable;
    this.built = false;
    this.enabled = true;

    this.playing = false;
    this.intensity = 0.35;
    this.intensityTarget = 0.35;
    this.manualUntil = -1;
    this.manualValue = -1;

    /** Global 16th-note counter. Model state, advances with or without audio. */
    this.step = 0;
    this.nextStepTime = 0;
    this.bpm = M.bpm;
    this.barBpm = M.bpm;
    /** Model clock used when there is no audio context. */
    this.clock = 0;
    this.scheduledNotes = 0;

    // Pre-drawn humanisation tables. Indexed by step so the groove is repeatable.
    this.velTable = new Float32Array(257);
    this.timeTable = new Float32Array(257);
    for (let i = 0; i < 257; i++) {
      this.velTable[i] = 1 + this.rand.bi() * M.velJitter;
      this.timeTable[i] = this.rand.bi() * (M.timingJitterMs / 1000);
    }
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;

    this.out = a.createGain();
    this.out.gain.value = 0.0001;
    // Bus highpass — see AUDIO_TUNING.music.busHpHz for why this is not optional.
    this.busHp = a.createBiquadFilter();
    this.busHp.type = 'highpass';
    this.busHp.frequency.value = M.busHpHz;
    this.busHp.Q.value = M.busHpQ;
    this.out.connect(this.busHp);
    this.busHp.connect(g.bus.music);

    // Drum sub-bus with its own persistent gains, so the kit never competes with sfx for
    // voice slots — a stolen backbeat is far more noticeable than a stolen cone bonk.
    const mkGain = (v) => { const n = a.createGain(); n.gain.value = v; n.connect(this.out); return n; };
    this.drumGain = {
      kick: mkGain(M.trim.kick),
      snare: mkGain(M.trim.snare),
      hat: mkGain(M.trim.hat),
      crash: mkGain(M.trim.crash),
      tom: mkGain(M.trim.tom),
      rim: mkGain(M.trim.rim),
    };
    // Kit glue: a gentle compressor on the drums only.
    this.kitComp = a.createDynamicsCompressor();
    this.kitComp.threshold.value = -18;
    this.kitComp.ratio.value = 3.5;
    this.kitComp.attack.value = 0.003;
    this.kitComp.release.value = 0.12;
    this.kitComp.connect(this.out);
    for (const k of Object.keys(this.drumGain)) {
      this.drumGain[k].disconnect();
      this.drumGain[k].connect(this.kitComp);
    }

    this.bassBus = mkGain(M.trim.bass);
    this.gtrBus = mkGain(M.trim.guitar);
    this.leadBus = mkGain(M.trim.lead);

    this.bassVoices = [];
    for (let i = 0; i < M.poly.bass; i++) this.bassVoices.push(new SynthVoice(a, this.bassBus, M.bassSynth, 1));
    this.gtrVoices = [];
    for (let i = 0; i < M.poly.guitar; i++) {
      this.gtrVoices.push(new SynthVoice(a, this.gtrBus, M.guitarSynth, 3, M.guitarSynth.detuneCents));
    }
    this.leadVoices = [];
    for (let i = 0; i < M.poly.lead; i++) this.leadVoices.push(new SynthVoice(a, this.leadBus, M.leadSynth, 1));

    this._bassRR = 0; this._gtrRR = 0; this._leadRR = 0;
    this._gtrFreqs = [110, 165, 220];
    this._monoFreq = [110];

    this.built = true;
  }

  // ---------------------------------------------------------------- control

  command(cmd) {
    if (typeof cmd !== 'string') return;
    if (cmd === 'start') this.start();
    else if (cmd === 'stop') this.stop();
    else if (cmd === 'pause') this.stop();
    else if (cmd.startsWith('intensity:')) {
      const v = parseFloat(cmd.slice(10));
      if (isFinite(v)) {
        this.manualValue = clamp01(v);
        this.manualUntil = this._now() + M.manualHoldS;
      }
    } else if (cmd === 'auto') {
      this.manualUntil = -1;
      this.manualValue = -1;
    }
  }

  start() {
    if (this.playing) return;
    this.playing = true;
    this.nextStepTime = this._now() + 0.06;
    if (this.graph.live && this.built) {
      const t = this.graph.actx.currentTime;
      const g = this.out.gain;
      g.cancelScheduledValues(t);
      g.setValueAtTime(Math.max(0.0001, g.value), t);
      g.linearRampToValueAtTime(this._masterGain(), t + M.fadeIn);
    }
  }

  stop() {
    if (!this.playing) return;
    this.playing = false;
    if (this.graph.live && this.built) {
      const t = this.graph.actx.currentTime;
      const g = this.out.gain;
      g.cancelScheduledValues(t);
      g.setValueAtTime(Math.max(0.0001, g.value), t);
      g.linearRampToValueAtTime(0.0001, t + M.fadeOut);
      for (const v of this.bassVoices) v.silence(t + M.fadeOut);
      for (const v of this.gtrVoices) v.silence(t + M.fadeOut);
      for (const v of this.leadVoices) v.silence(t + M.fadeOut);
    }
  }

  /** Automatic intensity from combo, timer pressure and speed. */
  setAuto(combo, timeRemaining, speedKmh) {
    const A = M.auto;
    const combo01 = clamp01(combo / A.comboFull);
    const time01 = clamp01((A.timeFrom - timeRemaining) / A.timeFrom);
    const speed01 = clamp01(speedKmh / A.speedFull);
    this.autoIntensity = clamp01(A.base + A.comboWeight * combo01 + A.timeWeight * time01 + A.speedWeight * speed01);
  }

  _masterGain() {
    const ig = M.intensityGain;
    return ig.min + (ig.max - ig.min) * this.intensity;
  }

  _now() { return this.graph.live ? this.graph.actx.currentTime : this.clock; }

  // ---------------------------------------------------------------- scheduling

  update(dt) {
    if (!this.graph.live) this.clock += dt;
    const now = this._now();

    // Intensity: manual overrides auto for a while, then it hands back.
    const wanted = (this.manualUntil > now && this.manualValue >= 0)
      ? this.manualValue
      : (this.autoIntensity != null ? this.autoIntensity : this.intensityTarget);
    this.intensityTarget = wanted;
    const slew = M.intensitySlew * dt;
    const d = this.intensityTarget - this.intensity;
    this.intensity += Math.abs(d) <= slew ? d : Math.sign(d) * slew;

    if (!this.playing) return;

    if (this.graph.live && this.built && this.enabled) {
      this.graph.param(this.out.gain, this._masterGain(), 0.25);
      // Fixed lookahead scheduler.
      const horizon = now + M.lookahead;
      let guard = 0;
      while (this.nextStepTime < horizon && guard++ < 96) {
        this._scheduleStep(this.step, this.nextStepTime);
        this._advance();
      }
    } else {
      // No audio: still advance the model clock so `stats()` and the form position are real.
      let guard = 0;
      while (this.nextStepTime < now && guard++ < 96) this._advance();
    }
  }

  _advance() {
    if (this.step % STEPS_PER_BAR === 0) {
      // Latch tempo at bar boundaries only — a tempo slide mid-bar sounds broken.
      this.barBpm = M.bpm + (M.bpmHigh - M.bpm) * this.intensity;
    }
    const stepSeconds = 60 / this.barBpm / 4;
    this.nextStepTime += stepSeconds;
    this.step++;
  }

  /**
   * Current bar's chord and section, derived purely from the step counter.
   * Writes into a caller-supplied scratch object — N9 forbids allocating in the frame path,
   * and the scheduler calls this twice per sixteenth note.
   */
  positionAt(step, out) {
    const o = out || (this._posScratch || (this._posScratch = { bar: 0, formIndex: 0, pass: 0, section: null, chord: null, sub: 0 }));
    const bar = Math.floor(step / STEPS_PER_BAR);
    const formIndex = bar % M.form.length;
    const pass = Math.floor(bar / M.form.length);
    o.bar = bar;
    o.formIndex = formIndex;
    o.pass = pass;
    o.section = M.sections[pass % M.sections.length];
    o.chord = M.chords[M.form[formIndex]];
    o.sub = step % STEPS_PER_BAR;
    return o;
  }

  _scheduleStep(step, when) {
    const p = this.positionAt(step, this._posA || (this._posA = {}));
    const sec = p.section;
    const inten = this.intensity;
    const jitter = this.timeTable[step % 257];
    const t = when + jitter;
    const vel = this.velTable[step % 257];
    const stepSeconds = 60 / this.barBpm / 4;

    // ---- drums
    const gHat = layerGain(M.layer.hat, inten);
    const gSnare = layerGain(M.layer.snare, inten);
    const gKick = layerGain(M.layer.kick, inten);
    const gCrash = layerGain(M.layer.crash, inten);

    if (gKick > 0 && sec.kick[p.sub] === '1') this._drum('kick', t, M.hit.kick * vel * gKick);
    if (gSnare > 0 && sec.snare[p.sub] === '1') {
      // Breakdown uses a rim instead of a full snare — same slot, different weight.
      if (sec.name === 'breakdown') this._drum('rim', t, M.hit.snare * vel * gSnare);
      else this._drum('snare', t, M.hit.snare * vel * gSnare);
    }
    if (gHat > 0) {
      if (sec.hatO[p.sub] === '1') this._drum('hatO', t, M.hit.hatAccent * vel * gHat);
      else if (sec.hatC[p.sub] === '1') {
        // Accent the downbeats so 16ths do not turn into a wash.
        const acc = p.sub % 4 === 0 ? M.hit.hatAccent : M.hit.hatWeak;
        this._drum('hatC', t, acc * vel * gHat);
      }
    }
    if (gCrash > 0 && p.sub === 0 && p.formIndex % 4 === 0 && sec.crashBar0) {
      this._drum('crash', t, 0.8 * gCrash);
    }
    // A tom fill on the last two beats of every eighth bar keeps the form breathing.
    if (gSnare > 0 && p.formIndex % 8 === 7 && p.sub >= 10 && p.sub % 2 === 0) {
      this._drum('tom', t, (0.5 + 0.12 * (p.sub - 10)) * vel * gSnare);
    }

    // ---- bass
    const gBass = layerGain(M.layer.bass, inten);
    if (gBass > 0 && sec.bass[p.sub] === '1') {
      const root = p.chord.root;
      // Octave jump on the "and" of 4 and a walk into the next bar on the last 16th.
      let note = root;
      if (p.sub === 14) {
        const next = this.positionAt(step + 2, this._posB || (this._posB = {})).chord.root;
        note = next > root ? root + 2 : root - 2;      // chromatic-ish approach
      } else if (p.sub === 8 && sec.name === 'drive') note = root + 12;
      const v = this.bassVoices[this._bassRR++ % this.bassVoices.length];
      this._monoFreq[0] = mtof(note);
      v.note(t, this._monoFreq, stepSeconds * 0.9, M.hit.bass * vel * gBass);
      this.scheduledNotes++;
    }

    // ---- guitar power chords
    const gGtr = layerGain(M.layer.guitar, inten);
    if (gGtr > 0 && sec.guitar[p.sub] === '1') {
      const root = p.chord.root;
      const V = M.guitarSynth.voicing;
      this._gtrFreqs[0] = mtof(root + V[0]);
      this._gtrFreqs[1] = mtof(root + V[1]);
      this._gtrFreqs[2] = mtof(root + V[2]);
      const v = this.gtrVoices[this._gtrRR++ % this.gtrVoices.length];
      v.note(t, this._gtrFreqs, Math.max(0.06, sec.gtrLen * stepSeconds * 4), M.hit.guitar * vel * gGtr);
      this.scheduledNotes++;
    }

    // ---- lead hook
    const gLead = layerGain(M.layer.lead, inten);
    if (gLead > 0) {
      const off = M.leadPattern[p.sub];
      // Only on the second half of the form, so the hook is an arrival, not a constant.
      if (off >= 0 && p.formIndex >= 8) {
        const v = this.leadVoices[this._leadRR++ % this.leadVoices.length];
        this._monoFreq[0] = mtof(p.chord.root + M.leadSynth.octave + off);
        v.note(t, this._monoFreq, stepSeconds * 1.4, M.hit.lead * vel * gLead);
        this.scheduledNotes++;
      }
    }
  }

  _drum(name, when, gain) {
    const g = this.graph;
    if (!g.live) return;
    const buf = g.buffer(name === 'hatO' || name === 'hatC' ? name : name);
    if (!buf) return;
    const dest = name === 'hatC' || name === 'hatO' ? this.drumGain.hat : (this.drumGain[name] || this.drumGain.kick);
    const a = g.actx;
    const src = a.createBufferSource();
    src.buffer = buf;
    // The kit needs a tiny per-hit gain, so one gain node per hit is unavoidable; it is two
    // nodes per drum, at most ~11 hits/second, and both are collected when the hit ends.
    const vg = a.createGain();
    vg.gain.value = Math.max(0.001, gain);
    src.connect(vg);
    vg.connect(dest);
    src.start(Math.max(when, a.currentTime));
    src.stop(Math.max(when, a.currentTime) + buf.duration + 0.01);
    this.scheduledNotes++;
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built) this.graph.param(this.out.gain, on && this.playing ? this._masterGain() : 0.0001, 0.05);
  }

  reset() {
    this.stop();
    this.step = 0;
    this.nextStepTime = 0;
    this.clock = 0;
    this.intensity = 0.35;
    this.intensityTarget = 0.35;
    this.autoIntensity = null;
    this.manualUntil = -1;
    this.manualValue = -1;
    this.scheduledNotes = 0;
  }

  stats() {
    const p = this.positionAt(this.step, this._posC || (this._posC = {}));
    return {
      playing: this.playing,
      intensity: +this.intensity.toFixed(3),
      bpm: +this.barBpm.toFixed(1),
      bar: p.bar,
      chord: M.form[p.formIndex],
      section: p.section.name,
      notes: this.scheduledNotes,
    };
  }
}
